import WebModel from '../models/WebModel'
import jump from 'jump.js'
import SlotFactory from '../slots/SlotFactory'
import PrebidFacade from '../prebidFacade/PrebidFacade'
import SlotRepository from '../slots/SlotRepository'
import { Slot } from '../slots/Slot'
import { Arrival } from '../slots'
import { ExecutionStatus } from '../wrapper'

const continueButtonSelector = '.mv-arrival-continue-button'

export class ArrivalSlot {
  static jtrClicked: boolean = false
  static arrivalSlot: Arrival | undefined | null
  static readonly jtrSelector: string =
    '.tasty-recipes-jump-link, .wprm-recipe-jump'

  constructor(
    readonly model: WebModel,
    readonly slotFactory: SlotFactory,
    readonly prebidFacade: PrebidFacade
  ) {}

  /**
   * The Arrival slot listener has to be set up before
   * the main load function is called. Otherwise, it would
   * not get triggered when a site has pagespeed optimize on
   * AND the user didn't scroll before clicking.
   * So we use a flag, jtrClicked to indicate to the render
   * func if it needs to proceed with rendering.
   */
  render() {
    if (
      ArrivalSlot.jtrClicked &&
      !ArrivalSlot.arrivalSlot &&
      this.model.jtr_arrival_unit_enabled
    ) {
      const target = ArrivalSlot.getJtrButton(this.model)
      if (!target) {
        return
      }
      this.onJTRClick(target as HTMLElement)
    }
  }

  static getJtrButton(model: WebModel) {
    const jtrSelector = model.jtr_button_selector || ArrivalSlot.jtrSelector
    const target = document.querySelector(jtrSelector)

    return target
  }

  /** The spot we want to stick the Arrival Unit. */
  static getLocation(model: WebModel) {
    const target =
      model.jtr_selector ||
      ArrivalSlot.getJtrButton(model)?.getAttribute('href') ||
      ''
    const location = document.querySelector(target) as HTMLElement

    return location
  }

  static attachListener(
    model: WebModel,
    loadFunc: () => Promise<ExecutionStatus>
  ) {
    if (!model.jtr_arrival_unit_enabled) {
      return
    }

    const target = ArrivalSlot.getJtrButton(model)

    if (!target) {
      return
    }

    const callback = async (e: Event) => {
      if (this.jtrClicked) {
        ArrivalSlot.scrollToArrivalSlot()
      }

      this.jtrClicked = true
      const loadStatus = await loadFunc()

      // If the wrapper fails to load, we want the default JTR button action to remain.
      // If the wrapper successfully loads, we need to prevent the default action.
      if (loadStatus !== ExecutionStatus.halted) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    target.addEventListener('click', callback)
    // target.addEventListener('touchstart', callback)
  }

  onJTRClick(target: HTMLElement) {
    const location = ArrivalSlot.getLocation(this.model)
    if (!location) {
      return
    }

    if (!ArrivalSlot.arrivalSlot) {
      ArrivalSlot.arrivalSlot = this.createArrivalSlot(location)
      this.addContinueButtonListener(location)
      this.moveTastyPrintButton(ArrivalSlot.arrivalSlot)
      this.auctionSlots()
    }

    ArrivalSlot.scrollToArrivalSlot()
  }

  static scrollToArrivalSlot() {
    const offsetForContinueButton = -160
    const jumpDuration = 3000
    const secondJumpDuration = 500
    const callback = () => {
      jump(this.arrivalSlot?.wrapper, {
        duration: secondJumpDuration,
        easing: easeInOutQuad,
        offset: offsetForContinueButton,
        callback: () => window.$mediavine.web.setContentWaypointDisable(false)
      })
    }

    if (window.LazyLoad && this.arrivalSlot) {
      const destination = this.arrivalSlot.wrapper.getBoundingClientRect().top
      scrollTo({ top: destination })
      callback()

      return
    }

    window.$mediavine.web.setContentWaypointDisable(true)

    /**
     * This double jump gives a fancy little
     * bounce back effect.
     */
    jump(this.arrivalSlot?.wrapper, {
      duration: jumpDuration,
      callback
    })
  }

  /**
   * Arrival slot has a little "Continue To Content" button.  This
   * listener takes us to the recipe card when it gets clicked.
   */

  private addContinueButtonListener(target: HTMLElement) {
    const continueButton = document.querySelector(continueButtonSelector)
    if (!continueButton) {
      return
    }
    continueButton.addEventListener('click', (e) => {
      e.preventDefault()
      jump(target, {
        duration: 500,
        offset: 300,
        easing: easeInOutQuad
      })
    })
  }

  private moveTastyPrintButton(slot: Slot | undefined) {
    if (!slot) {
      return
    }
    const printButton = document.querySelector('.tasty-recipes-print-button')
    if (!printButton) {
      return
    }
    printButton.remove()
    slot.wrapper.insertAdjacentElement('afterend', printButton)
  }

  /** Auction our arrival slot plus any Recipe slots while we travel down the page */
  private auctionSlots() {
    if (!ArrivalSlot.arrivalSlot) {
      return
    }
    const recipeSlots = SlotRepository.getRecipeSlots().filter((s) => !s.adType)
    this.prebidFacade.requestBids(
      [ArrivalSlot.arrivalSlot, ...recipeSlots],
      ArrivalSlot.arrivalSlot.timeout.value
    )
  }

  private createArrivalSlot(location: HTMLElement): Arrival | undefined {
    return this.slotFactory.new({
      adUnitId: 'arrival',
      insertPosition: 'beforebegin',
      selector: location,
      type: 'Arrival'
    }) as Arrival
  }
}

const easeInOutQuad = (t, b, c, d) => {
  t /= d / 2
  if (t < 1) return (c / 2) * t * t + b
  t--
  return (-c / 2) * (t * (t - 2) - 1) + b
}
